import { useCallback, useRef, useState } from "react";
import { useQuery } from "react-query";
import * as openFactsService from 'services/openFactsService';
import { ImportDoneEvent, ImportEvent, ImportOpenFactsDto, ImportProductEvent } from "services/openFactsService";
import { ListBeautyProductsDto } from "types/dto";


export const useGetBeautyProducts = (dto: ListBeautyProductsDto, options = {}) => {
    return useQuery({
        queryKey: ['beautyProducts', dto],
        queryFn: () => openFactsService.getBeautyProducts(dto),
        keepPreviousData: true,
        ...options
    });
}

export type ImportPhase = 'idle' | 'running' | 'done';

export const useImportOpenFactsStream = () => {
    const [phase, setPhase] = useState<ImportPhase>('idle');
    const [events, setEvents] = useState<ImportProductEvent[]>([]);
    const [summary, setSummary] = useState<ImportDoneEvent | null>(null);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const run = useCallback(async (dto: ImportOpenFactsDto) => {
        const controller = new AbortController();
        abortRef.current = controller;
        setPhase('running');
        setEvents([]);
        setSummary(null);
        setTotal(dto.size);
        setError(null);
        try {
            await openFactsService.importOpenFactsStream(
                dto,
                (event: ImportEvent) => {
                    if (event.type === 'product') setEvents(prev => [...prev, event]);
                    else if (event.type === 'done') setSummary(event);
                },
                controller.signal,
            );
        } catch (err: any) {
            if (err?.name !== 'AbortError') setError(err?.message ?? 'Import failed');
        } finally {
            setPhase('done');
        }
    }, []);

    const stop = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    const reset = useCallback(() => {
        setPhase('idle');
        setEvents([]);
        setSummary(null);
        setError(null);
    }, []);

    return { phase, events, summary, total, error, run, stop, reset };
}